import { UIPriority } from '@/enums/ui-priorities';
import type {
    ClientAiQuotaFeatureAvailabilityEntity,
    ClientAiQuotaSummaryEntity,
} from '@/entities/client-ai-quota.entity';
import { isClientAiQuotaExhaustedAvailability } from '@/shared/client-ai-quota';
import type { ActionButtonsActiveTable } from '@/components/action-buttons/action-buttons-view.types';

const resolveCountBadgeClassName = (count: number) => (count > 99 ? 'min-w-6 px-1' : 'w-4');

type UseActionButtonsViewModelArgs = {
    activeTable: ActionButtonsActiveTable;
    pickedReviewsCount: number;
    pickedProductsCount: number;
    pickedBrandGroupsCount: number;
    allowResponseSending: boolean;
    aiQuota: ClientAiQuotaSummaryEntity;
    aiAnswerAvailability: ClientAiQuotaFeatureAvailabilityEntity;
    aiAnalysisAvailability: ClientAiQuotaFeatureAvailabilityEntity;
    isAiAnalysisLoading: boolean;
    isCombinedAnalysisLoading: boolean;
    isGroupsMergeLoading: boolean;
};

export const useActionButtonsViewModel = ({
    activeTable,
    pickedReviewsCount,
    pickedProductsCount,
    pickedBrandGroupsCount,
    allowResponseSending,
    aiQuota,
    aiAnswerAvailability,
    aiAnalysisAvailability,
    isAiAnalysisLoading,
    isCombinedAnalysisLoading,
    isGroupsMergeLoading,
}: UseActionButtonsViewModelArgs) => {
    const isReviewsTable = activeTable === 'reviews';
    const isProductsTable = activeTable === 'products';
    const isBrandsTable = activeTable === 'brands';
    const isGroupTable = isProductsTable || isBrandsTable;

    const hasPickedReviews = pickedReviewsCount > 0;
    const hasPickedProducts = pickedProductsCount > 0;
    const hasPickedBrandGroups = pickedBrandGroupsCount > 0;
    const hasPickedActiveGroup = isBrandsTable ? hasPickedBrandGroups : hasPickedProducts;
    const canMergePickedProducts = pickedProductsCount >= 2;
    const canMergePickedBrandGroups = pickedBrandGroupsCount >= 2;
    const canMergeActiveGroup = isBrandsTable ? canMergePickedBrandGroups : canMergePickedProducts;

    const selectedItemsCount = isProductsTable
        ? pickedProductsCount
        : isBrandsTable
          ? pickedBrandGroupsCount
          : pickedReviewsCount;
    const selectedItemsCountBadgeClassName = resolveCountBadgeClassName(selectedItemsCount);

    const reviewsExportCountBadgeClassName = resolveCountBadgeClassName(pickedReviewsCount);
    const groupsExportCount = isBrandsTable ? pickedBrandGroupsCount : pickedProductsCount;
    const groupsExportCountBadgeClassName = resolveCountBadgeClassName(groupsExportCount);

    const groupSelectionPriority = hasPickedActiveGroup ? UIPriority.PRIMARY : UIPriority.SECONDARY;
    const mergePriority = canMergeActiveGroup ? UIPriority.PRIMARY : UIPriority.SECONDARY;
    const reviewsSelectionPriority = hasPickedReviews ? UIPriority.PRIMARY : UIPriority.SECONDARY;
    const allowAiAnswerGeneration = aiAnswerAvailability.enabled;
    const allowAiAnalysis = aiAnalysisAvailability.enabled;
    const aiAnswerGenerationDisabledReason = aiAnswerAvailability.reason;
    const aiAnalysisDisabledReason = aiAnalysisAvailability.reason;

    const isReplyDisabled = hasPickedReviews
        ? !allowResponseSending && !allowAiAnswerGeneration
        : !allowAiAnswerGeneration;
    const replyDisabledReason = isReplyDisabled && !allowAiAnswerGeneration ? aiAnswerGenerationDisabledReason : null;
    const isReplyQuotaExhausted = isReplyDisabled && isClientAiQuotaExhaustedAvailability(aiAnswerAvailability);
    const isAccountLimitZero = Object.values(aiQuota.metrics).every((metric) => metric.remaining <= 0);
    const isAiAnalysisForReviewsDisabled = !allowAiAnalysis || isAiAnalysisLoading;
    const isAiAnalysisForGroupDisabled = !allowAiAnalysis || !hasPickedActiveGroup || isAiAnalysisLoading;
    const isAiAnalysisQuotaExhausted = isClientAiQuotaExhaustedAvailability(aiAnalysisAvailability);
    const isCombinedAnalysisForGroupDisabled = !hasPickedActiveGroup || isCombinedAnalysisLoading;
    const isMergeActiveGroupDisabled = !canMergeActiveGroup || isGroupsMergeLoading;

    return {
        isReviewsTable,
        isGroupTable,
        isBrandsTable,
        hasPickedReviews,
        hasPickedProducts,
        hasPickedBrandGroups,
        selectedItemsCount,
        selectedItemsCountBadgeClassName,
        reviewsExportCountBadgeClassName,
        groupsExportCount,
        groupsExportCountBadgeClassName,
        groupSelectionPriority,
        mergePriority,
        reviewsSelectionPriority,
        isReplyDisabled,
        replyDisabledReason,
        isReplyQuotaExhausted,
        isAccountLimitZero,
        isAiAnalysisForReviewsDisabled,
        isAiAnalysisForGroupDisabled,
        aiAnalysisDisabledReason,
        isAiAnalysisQuotaExhausted,
        isCombinedAnalysisForGroupDisabled,
        isMergeActiveGroupDisabled,
    };
};
